// Dateien speichern (Mobil-Spezifikation §4.1–4.3): Im Browser wie bisher per Blob und <a download>; in der
// Merxo-App über die Brücke (MerxoNative.shareFile – Teilen-Blatt bzw. Dialog „Teilen/Speichern“), denn die WebView
// speichert keine Downloads. Zusammen mit web/api/files.tsx (saveUrl, DownloadLink) und web/ui/print.ts die einzige
// Stelle der Oberfläche, die Downloads auslöst oder neue Fenster öffnet (Wächter-Test web/lib/native.guard.test.ts).
// Module nehmen saveFile, downloadCsv oder DownloadLink aus @api.
import {
  NATIVE_MAX_FILE_BYTES,
  nativeApp,
  NativeError,
  nativeRect,
  nativeShareFile,
  type NativeRect,
  type NativeShareFileResult,
} from '../lib/native'
import { lastPressed } from '../lib/pressed'
import { toast } from './Toast'
import { brand } from '@shared/brand'

export type SaveFileData = Blob | ArrayBuffer | Uint8Array | string | { base64: string }

export interface SaveFileOptions {
  /** mit Endung, z. B. 'artikel-2026-10-15.csv' */
  fileName: string
  /** z. B. 'text/csv;charset=utf-8' */
  mimeType: string
  /** Inhalt; schon vorhandenes Base64 als { base64 } (wird nicht neu kodiert) */
  data: SaveFileData
  /** Überschrift des Android-Dialogs (Standard: Dateiname) */
  title?: string
  /** auslösendes Element bzw. Rechteck – Anker des Teilen-Popovers auf dem iPad (Standard: zuletzt gedrückte Schaltfläche) */
  anchor?: Element | NativeRect | null
}

/** browser: klassischer Download · saved/shared/presented/cancelled: Ergebnis der App · failed: Meldung gezeigt */
export type SaveFileOutcome = 'browser' | NativeShareFileResult['outcome'] | 'failed'

export const SAVE_FILE_MESSAGES = {
  saved: 'Datei gespeichert',
  tooLarge: 'Die Datei ist zu groß für die App (über 25 MB). Bitte laden Sie sie im Browser herunter.',
  failed: 'Die Datei konnte nicht übergeben werden.',
  update: `Bitte aktualisieren Sie die ${brand.name}-App, um diese Funktion zu nutzen.`,
  fetchFailed: 'Die Datei konnte nicht geladen werden.',
} as const

const isBase64 = (data: SaveFileData): data is { base64: string } =>
  typeof data === 'object' && !(data instanceof Blob) && !(data instanceof Uint8Array) && !(data instanceof ArrayBuffer)

const toBlob = (data: SaveFileData, mimeType: string): Blob => {
  if (data instanceof Blob) return data
  if (typeof data === 'string') return new Blob([data], { type: mimeType })
  if (data instanceof Uint8Array) return new Blob([data.slice()], { type: mimeType })
  if (!isBase64(data)) return new Blob([data], { type: mimeType })
  const binary = atob(data.base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index)
  return new Blob([bytes], { type: mimeType })
}

/** Base64 eines Blobs über FileReader.readAsDataURL – Byte-genau, auch mit BOM und Umlauten (kein btoa über Text). */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('Datei nicht lesbar'))
    reader.onload = () => {
      const url = String(reader.result ?? '')
      const comma = url.indexOf(',')
      resolve(comma >= 0 ? url.slice(comma + 1) : '')
    }
    reader.readAsDataURL(blob)
  })
}

/**
 * Anker des Popovers: das übergebene Element bzw. Rechteck, sonst die zuletzt gedrückte Schaltfläche (web/lib/pressed –
 * WebKit fokussiert beim Tippen nichts), zuletzt das Element im Fokus.
 */
export const anchorOf = (anchor: Element | NativeRect | null | undefined): NativeRect | undefined => {
  if (anchor && !(anchor instanceof Element)) return anchor
  const focused = typeof document !== 'undefined' && document.activeElement !== document.body ? document.activeElement : null
  return nativeRect(anchor) ?? nativeRect(lastPressed()) ?? nativeRect(focused)
}

function browserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Meldung zu einer abgelehnten Übergabe (§4.3). BUSY: nichts – der Dialog ist schon offen. */
export function showShareError(error: unknown): void {
  const code = error instanceof NativeError ? error.code : 'FAILED'
  if (code === 'BUSY') return
  if (code === 'TOO_LARGE') {
    toast.show({ title: SAVE_FILE_MESSAGES.tooLarge, tone: 'danger', id: 'save-file' })
    return
  }
  const message = error instanceof Error ? error.message : ''
  toast.show({ title: SAVE_FILE_MESSAGES.failed, ...(message ? { description: message } : {}), tone: 'danger', id: 'save-file' })
}

/**
 * Datei speichern: Browser → Download wie bisher; App → Teilen/Sichern über die Brücke.
 * Wirft nie – Fehler zeigt ein Toast; das Ergebnis sagt, was geschehen ist.
 */
export async function saveFile(options: SaveFileOptions): Promise<SaveFileOutcome> {
  const { fileName, mimeType, data } = options
  if (!nativeApp()) {
    browserDownload(toBlob(data, mimeType), fileName)
    return 'browser'
  }
  // Größenbremse VOR der Brücke (mobile-nativ-01 Nr. 2): Android kopiert die Nachricht mehrfach – 30 MB als Base64
  // brachten die App an die Speichergrenze, bevor das Plugin „zu groß“ melden konnte
  const bytes = isBase64(data) ? Math.floor((data.base64.replace(/\s+/g, '').replace(/=+$/, '').length * 3) / 4) : toBlob(data, mimeType).size
  if (bytes > NATIVE_MAX_FILE_BYTES) {
    showShareError(new NativeError('TOO_LARGE', SAVE_FILE_MESSAGES.tooLarge))
    return 'failed'
  }
  try {
    const base64 = isBase64(data) ? data.base64.replace(/\s+/g, '') : await blobToBase64(toBlob(data, mimeType))
    const anchor = anchorOf(options.anchor)
    const result = await nativeShareFile({
      fileName,
      mimeType,
      base64,
      ...(options.title ? { title: options.title } : {}),
      ...(anchor ? { anchor } : {}),
    })
    if (!result) {
      // alte App-Fassung ohne shareFile: Hinweis und der Weg des Browsers (die Hülle fängt Downloads bestmöglich ab)
      toast.show({ title: SAVE_FILE_MESSAGES.update, tone: 'warning', id: 'save-file' })
      browserDownload(toBlob(data, mimeType), fileName)
      return 'browser'
    }
    if (result.outcome === 'saved') toast.show({ title: SAVE_FILE_MESSAGES.saved, tone: 'success', id: 'save-file' })
    return result.outcome
  } catch (error) {
    showShareError(error)
    return 'failed'
  }
}

/**
 * Adresse in einem neuen Fenster bzw. Tab öffnen – nur im Browser. In der App gibt es keine neuen Fenster: iOS öffnete
 * jedes `_blank` in Safari, ohne Sitzung (Spezifikation §5.2). Rückgabe false: nicht geöffnet (App) – der Aufrufer
 * wählt den Weg der App (gleiches Fenster bzw. Teilen-Blatt).
 */
export function openInNewWindow(href: string): boolean {
  if (nativeApp()) return false
  window.open(href, '_blank', 'noopener')
  return true
}
